import { useState } from 'react';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import {
  CheckCircleIcon,
  CircleNotchIcon,
  IdentificationCardIcon,
  SparkleIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import type { OnboardingView } from '@logspot-platform/platform-types';
import { PageContent } from '@/components/page-content';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CompanyLogo } from '@/components/profiles/company-profile-bits';
import { ProfileWizard } from '@/components/profiles/profile-wizard';
import {
  LaunchChecklist,
  SetupProgress,
  useProfileBuild,
  useCanActOnSetup,
} from '@/components/onboarding/launch-checklist';
import { OnboardingTile } from '@/components/onboarding/onboarding-tile';
import { PlanSelectorSheet } from '@/components/settings/plan-selector-sheet';
import {
  featuresOptions,
  invalidatePlanQueries,
  onboardingOptions,
} from '@/hooks/query-options';
import { useAccountDetails } from '@/hooks/use-account-details';
import { useFeatures } from '@/hooks/use-features';
import { useOnboarding } from '@/hooks/use-onboarding';
import { useProjects } from '@/hooks/use-projects';
import { useSession } from '@/lib/auth-client';
import { pricingLinkSearch } from '@/lib/plans';

export const Route = createFileRoute('/_auth/_side-layout/getting-started')({
  validateSearch: z.object({
    checkout: z.enum(['success', 'cancelled']).optional(),
    // A signed-in user who followed a pricing link lands here with the plan preselected.
    ...pricingLinkSearch,
  }),
  beforeLoad: async ({ context, search }) => {
    const features =
      await context.queryClient.ensureQueryData(featuresOptions());
    if (features.leanOnboarding !== true) throw redirect({ to: '/' });
    if (search.checkout === 'success') {
      // Stripe sent them back from the fast-lane checkout: refresh plan, billing and credits.
      await invalidatePlanQueries(context.queryClient);
    }
  },
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(onboardingOptions()),
  component: GettingStarted,
});

function ProfileRow({ view }: { view: OnboardingView }) {
  const { isEnabled } = useFeatures();
  const { effectivePlan } = useAccountDetails();
  const { project } = useProjects();
  const { building, profile, failed } = useProfileBuild(view);
  const [wizardOpen, setWizardOpen] = useState(false);
  const website = project?.domainWhitelist?.[0] ?? '';
  const canBuild =
    isEnabled('ai') &&
    isEnabled('companyProfile') &&
    (effectivePlan !== 'FREE' || isEnabled('freePlanProfileBuild'));

  if (profile) {
    return (
      <div className="bg-card flex flex-wrap items-center gap-3.5 rounded border p-4">
        <CompanyLogo
          domain={profile.domain}
          name={profile.label}
          className="size-10"
        />
        <div className="flex min-w-0 flex-[1_1_280px] flex-col gap-0.5">
          <span className="truncate text-base font-semibold">
            {profile.label}
          </span>
          <span className="text-muted-foreground line-clamp-2 text-sm">
            {profile.one_liner || 'Company profile ready.'}
          </span>
        </div>
        <Button size="sm" variant="outline" asChild>
          <Link
            to="/actions/profiles/$profileId"
            params={{ profileId: profile.id }}
          >
            Review Profile
          </Link>
        </Button>
      </div>
    );
  }

  if (building) {
    return (
      <div className="bg-card flex items-center gap-3.5 rounded border p-4">
        <OnboardingTile className="size-10 [&_svg]:size-5">
          <IdentificationCardIcon />
        </OnboardingTile>
        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-base font-semibold">
            Building Your Company Profile
          </span>
          <span className="text-muted-foreground text-sm">
            Researching {website || 'your website'}. Keep going; it lands here
            when it's ready.
          </span>
        </div>
        <Badge>
          <CircleNotchIcon className="animate-spin" />
          Researching
        </Badge>
      </div>
    );
  }

  if (!canBuild) return null;
  return (
    <div className="bg-card flex flex-wrap items-center gap-3.5 rounded border p-4">
      <OnboardingTile className="size-10 [&_svg]:size-5">
        {failed ? <WarningCircleIcon /> : <SparkleIcon />}
      </OnboardingTile>
      <div className="flex min-w-0 flex-[1_1_280px] flex-col gap-0.5">
        <span className="text-base font-semibold">
          {failed
            ? "We Couldn't Finish Your Profile"
            : 'Build Your Company Profile'}
        </span>
        <span className="text-muted-foreground text-sm">
          {failed
            ? 'The research run stopped early. Try again; failed builds stay free.'
            : 'Logspot researches your company from its website. Your first build is free.'}
        </span>
      </div>
      <Button size="sm" onClick={() => setWizardOpen(true)}>
        {failed ? 'Try Again' : 'Build My Profile'}
      </Button>
      <ProfileWizard
        open={wizardOpen}
        onOpenChange={setWizardOpen}
        initialWebsite={website}
      />
    </div>
  );
}

const completeSummary = (done: number, skipped: number): string => {
  const steps = (n: number) => `${n} ${n === 1 ? 'step' : 'steps'}`;
  if (skipped === 0) return `All ${steps(done)} done.`;
  if (done === 0)
    return 'Every step was skipped. Reopen any of them anytime below.';
  return `${steps(done)} done and ${skipped} skipped. Reopen a skipped step anytime below.`;
};

function GettingStarted() {
  const { view } = useOnboarding();
  const { checkout, plan, interval, events } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { data: session } = useSession();
  const { currentPlan } = useAccountDetails();
  const { canBuy } = useCanActOnSetup();
  if (!view) return null;

  const firstName = session?.user?.name?.split(' ')[0];
  const { resolved, total } = view.progress;
  const allSet = resolved === total;
  const skipped = view.tasks.filter((task) => task.status === 'skipped').length;
  const done = view.tasks.filter((task) => task.status === 'done').length;
  const greeting = allSet ? "You're All Set" : 'Welcome to Logspot';

  return (
    <PageContent>
      <div className="mx-auto flex w-full max-w-3xl flex-col gap-6">
        {checkout === 'success' && (
          <Alert
            variant="success"
            title="Payment Succeeded"
            subTitle="Your plan switches on as soon as Stripe confirms the payment. Your Data Credits show in Billing."
          />
        )}
        {checkout === 'cancelled' && (
          <Alert
            title="Checkout Cancelled"
            subTitle="Nothing was charged. Your trial is still on, and Choose a Plan is in the banner above."
          />
        )}
        <div className="flex flex-col gap-1.5">
          <h1 className="text-xl font-bold tracking-tight">
            {firstName ? `${greeting}, ${firstName}` : greeting}
          </h1>
          <p className="text-muted-foreground text-sm">
            {allSet
              ? 'Every step is done or skipped. The rocket stays in the rail for new steps and anything you reopen.'
              : 'Your company profile comes first. The other steps check themselves off as Logspot sees the data.'}
          </p>
        </div>
        <SetupProgress view={view} />
        <ProfileRow view={view} />
        {allSet && (
          <div className="border-chart-green/30 bg-chart-green/10 flex flex-wrap items-center gap-3.5 rounded border p-5">
            <CheckCircleIcon
              weight="fill"
              className="text-chart-green size-7 shrink-0"
            />
            <div className="flex min-w-0 flex-[1_1_280px] flex-col gap-0.5">
              <span className="text-base font-semibold">Setup Complete</span>
              <span className="text-muted-foreground text-sm">
                {completeSummary(done, skipped)}
              </span>
            </div>
            <Button asChild>
              <Link to="/">Go to Dashboard</Link>
            </Button>
          </div>
        )}
        <div className="mt-2 flex items-baseline justify-between">
          <h2 className="text-base font-semibold">Setup Checklist</h2>
          <span className="text-muted-foreground text-sm font-medium">
            {allSet ? 'All Resolved' : `${total - resolved} Left`}
          </span>
        </div>
        <LaunchChecklist view={view} />
      </div>
      {plan && canBuy && (
        <PlanSelectorSheet
          open
          onOpenChange={(open) => {
            if (!open) {
              navigate({
                search: (prev) => ({
                  ...prev,
                  plan: undefined,
                  interval: undefined,
                  events: undefined,
                }),
              });
            }
          }}
          currentPlan={currentPlan}
          initialTier={plan === 'pro' ? 'PRO' : 'ESSENTIALS'}
          initialInterval={interval}
          initialBucket={events}
          source="pricing_link"
        />
      )}
    </PageContent>
  );
}
